import mapboxgl from 'mapbox-gl';

import { toast } from '../toastService';
import ZoneamentoService from '../../../services/ZoneamentoService';
import { parseCidadeValue } from './cidade';
import { beginLayerLoading, endLayerLoading } from './layerLoading';

/**
 * Camada de zoneamento urbano (paridade com o mapa Google, DefaulMap.jsx),
 * compartilhada entre os mapas Mapbox.
 *
 * Contrato do componente hospedeiro:
 *  - `self.map`, `self.state`, `self.setState`;
 *  - `self.getCidadesSelecionadas()` → `[{ value: 'UF-Cidade', label }]`;
 *  - chamar `initZoneamento(self)` no construtor/componentDidMount;
 *  - espalhar `ZONEAMENTO_INITIAL_STATE` no estado inicial;
 *  - chamar `setupZoneamentoLayers(self)` e `reapplyZoneamento(self)` em `initializeLayers()`.
 */

const SOURCE_ID = 'zoneamento';
export const ZONEAMENTO_LAYERS = ['zoneamento-fill', 'zoneamento-outline'];
// As camadas filtram por feature (`visible`) para permitir recortar as zonas exibidas.
const VISIBLE_FILTER = ['==', ['get', 'visible'], true];
const EMPTY_FC = { type: 'FeatureCollection', features: [] };
const LOADING_LABEL = 'Carregando zoneamento…';

// Permissão `ver-zoneamento` injetada no page load (ver a blade de cada mapa).
export const CAN_VER_ZONEAMENTO = !!window.can_ver_zoneamento;

export const ZONEAMENTO_INITIAL_STATE = {
    showZoneamento: false,
    zoneamentoAvailable: false,
    legendaZoneamento: [],
    opcoesPropriedadesZoneamento: [],
    valoresPropriedadesZoneamento: {},
};

/** Cria os campos de instância que a feature mantém fora do estado. */
export function initZoneamento(self) {
    self.zoneamentoService = new ZoneamentoService();
    // Cache por cidade: o GeoJSON de zoneamento é grande e vem de uma API externa.
    self.zoneamentoCache = {};
    self.currentZoneamentoCidade = null;
}

/**
 * Source vazia + camadas de preenchimento/contorno. Espelha o estilo do mapa Google:
 * cor por sigla_zoneamento (`color`), opacidade 0.6, contorno azul.
 */
export function setupZoneamentoLayers(self) {
    if (self.map.getSource(SOURCE_ID)) return;

    self.map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC });

    self.map.addLayer({
        id: 'zoneamento-fill',
        type: 'fill',
        source: SOURCE_ID,
        filter: VISIBLE_FILTER,
        // coalesce: zona sem `color` (dado inesperado) cai num cinza visível em vez
        // do default do Mapbox.
        paint: { 'fill-color': ['coalesce', ['get', 'color'], '#9e9e9e'], 'fill-opacity': 0.6 },
    });

    self.map.addLayer({
        id: 'zoneamento-outline',
        type: 'line',
        source: SOURCE_ID,
        filter: VISIBLE_FILTER,
        paint: { 'line-color': '#0000ff', 'line-width': 1, 'line-opacity': 0.5 },
    });

    const zoneamentoPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });
    const labels = self.zoneamentoService.properties;

    self.map.on('click', 'zoneamento-fill', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const p = f.properties || {};

        let body = '';
        Object.entries(labels).forEach(([key, label]) => {
            const value = p[key];
            body += `
                    <div class="info-window-item">
                        <span class="info-window-item-label font-weight-bold">${label}:</span>
                        <span class="info-window-item-value">${value ?? ''}</span>
                    </div>`;
        });

        const content = `
                <div class="info-window">
                    <div class="info-window-header">
                        <h5 class="info-window-title">SIGLA ZONEAMENTO</h5>
                        <h6 class="info-window-subtitle">${p.sigla_zoneamento ?? ''}</h6>
                    </div>
                    <div class="info-window-body" style="max-height:400px;overflow-y:auto;">${body}</div>
                </div>`;

        zoneamentoPopup.setLngLat(e.lngLat).setHTML(content).addTo(self.map);
    });

    self.map.on('mouseenter', 'zoneamento-fill', () => { self.map.getCanvas().style.cursor = 'pointer'; });
    self.map.on('mouseleave', 'zoneamento-fill', () => { self.map.getCanvas().style.cursor = ''; });
}

/**
 * Obtém os dados de zoneamento da cidade (via cache de instância ou API externa),
 * já processados por prepareData (cores/legenda/opções de filtro).
 */
async function getZoneamentoData(self, cidadeOption) {
    const value = cidadeOption.value;
    if (self.zoneamentoCache[value]) return self.zoneamentoCache[value];

    const parsed = parseCidadeValue(value);
    if (!parsed) return null;

    const res = await self.zoneamentoService.getZoneamento({ cidade: parsed.cidade, estado: parsed.uf });
    if (res.status !== 200 || !res.data?.features) return null;

    const prepared = self.zoneamentoService.prepareData(res.data);
    self.zoneamentoCache[value] = prepared;
    return prepared;
}

export async function showZoneamentoLayer(self) {
    if (!self.map?.getSource(SOURCE_ID)) return;

    const cidades = self.getCidadesSelecionadas() || [];
    if (cidades.length === 0) {
        toast.info('Selecione e busque uma cidade para ver o zoneamento.');
        self.setState({ showZoneamento: false });
        return;
    }

    // Zoneamento é por cidade única (mesma regra do mapa Google: cidade[0]).
    const cidadeOption = cidades[0];

    beginLayerLoading(self, LOADING_LABEL);
    try {
        const prepared = await getZoneamentoData(self, cidadeOption);
        if (!prepared || !prepared.geojson?.features?.length) {
            toast.info('Nenhum dado de zoneamento encontrado para a cidade selecionada.');
            self.setState({ showZoneamento: false });
            return;
        }

        self.currentZoneamentoCidade = cidadeOption;
        self.map.getSource(SOURCE_ID).setData(prepared.geojson);

        self.setState({
            legendaZoneamento: prepared.legenda,
            opcoesPropriedadesZoneamento: prepared.opcoesDropdownProperties,
            valoresPropriedadesZoneamento: prepared.valoresPropriedades,
            active_tab: 'legendas',
        });
    } catch (e) {
        console.error('[zoneamento] erro ao carregar zoneamento:', e);
        toast.error('Erro ao carregar o zoneamento.');
        self.setState({ showZoneamento: false });
    } finally {
        endLayerLoading(self, LOADING_LABEL);
    }
}

/**
 * Recorta as zonas exibidas por propriedade/valor (permissao_horizontal/vertical),
 * alternando `visible` em cada feature; o filtro de camada oculta as não-visíveis.
 */
export function filtrarZoneamento(self, { property, value }) {
    const cidadeOption = self.currentZoneamentoCidade;
    const prepared = cidadeOption && self.zoneamentoCache[cidadeOption.value];
    if (!prepared?.geojson) return;

    prepared.geojson.features.forEach((feature) => {
        feature.properties.visible = feature.properties[property] === value;
    });

    self.map.getSource(SOURCE_ID)?.setData(prepared.geojson);
}

export function removeZoneamentoLayer(self) {
    self.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
    self.setState({
        legendaZoneamento: [],
        opcoesPropriedadesZoneamento: [],
        valoresPropriedadesZoneamento: {},
    });
}

export function toggleShowZoneamento(self, enabled) {
    self.setState({ showZoneamento: enabled });
    if (enabled) {
        showZoneamentoLayer(self);
    } else {
        removeZoneamentoLayer(self);
    }
}

/**
 * Verifica se a 1ª cidade selecionada tem zoneamento cadastrado (POST /zone-available),
 * gateando a exibição do switch. Espelha refreshBairrosAvailability.
 *
 * @param {Array<{value: string}>} cidades
 */
export async function refreshZoneamentoAvailability(self, cidades) {
    if (!CAN_VER_ZONEAMENTO) return;

    const cidadeOption = (cidades || [])[0];
    const parsed = cidadeOption && parseCidadeValue(cidadeOption.value);
    if (!parsed) {
        self.setState({ zoneamentoAvailable: false });
        return;
    }

    try {
        const res = await self.zoneamentoService.getZoneAvailability({ cidade: parsed.cidade, estado: parsed.uf });
        const available = !!res.data?.available;
        self.setState({ zoneamentoAvailable: available });

        if (!available && self.state.showZoneamento) {
            self.setState({ showZoneamento: false });
            removeZoneamentoLayer(self);
        }
    } catch (e) {
        console.error('[zoneamento] erro ao verificar disponibilidade:', e);
        self.setState({ zoneamentoAvailable: false });
    }
}

/**
 * Volta a feature ao estado inicial (usado pelo LIMPAR dos mapas): apaga a camada,
 * o cache da cidade exibida e o estado de UI.
 *
 * @returns {object} Os campos de estado a mesclar no `setState` do hospedeiro.
 */
export function resetZoneamento(self) {
    self.map?.getSource(SOURCE_ID)?.setData(EMPTY_FC);
    self.zoneamentoCache = {};
    self.currentZoneamentoCidade = null;
    return { ...ZONEAMENTO_INITIAL_STATE };
}

/**
 * `initializeLayers()` roda de novo a cada troca de estilo do mapa, que descarta
 * fontes e camadas — sem isto o zoneamento ligado desapareceria.
 */
export function reapplyZoneamento(self) {
    if (self.state.showZoneamento && self.currentZoneamentoCidade) {
        showZoneamentoLayer(self);
    }
}
